import { useEffect, useRef, useState, useCallback } from 'react';
import { onJuice, PALETTES } from './juice';
import './JuiceLayer.css';

/* ============================================================================
   JuiceLayer — renders everything the juice bus emits.

   Particles are plain DOM nodes animated by a single CSS keyframe reading
   per-particle custom properties, then reaped on a timer. That keeps the whole
   thing off the JS main loop: no rAF, no physics tick.

   Screen shake is applied to #root rather than rendered here, since the point
   is to move the content the player is looking at.
   ========================================================================== */

const SHAKE_MS = { light: 220, medium: 340, heavy: 520 };
const PARTICLE_MS = 900;
const RAIN_MS = 3200;
const FLOAT_MS = 1100;

function makeBurstParticles({ id, x, y, count, palette, power, spread, angle }) {
  const colors = PALETTES[palette] ?? PALETTES.fire;
  const particles = [];

  for (let i = 0; i < count; i += 1) {
    // Spread the cone evenly then jitter, so bursts look scattered but never
    // clump into a visible fan.
    const base = angle - spread / 2 + (spread * i) / count;
    const theta = ((base + (Math.random() - 0.5) * (spread / count) * 2) * Math.PI) / 180;
    const distance = (60 + Math.random() * 130) * power;

    particles.push({
      key: `${id}-${i}`,
      x,
      y,
      dx: Math.sin(theta) * distance,
      // Negative because screen Y grows downward and 0deg should mean "up".
      dy: -Math.cos(theta) * distance + Math.random() * 40,
      size: 4 + Math.random() * 9,
      rot: (Math.random() - 0.5) * 900,
      color: colors[Math.floor(Math.random() * colors.length)],
      delay: Math.random() * 60,
      square: Math.random() > 0.45,
    });
  }
  return particles;
}

function makeRainParticles({ id, count, palette }) {
  const colors = PALETTES[palette] ?? PALETTES.win;
  const particles = [];

  for (let i = 0; i < count; i += 1) {
    particles.push({
      key: `${id}-r${i}`,
      left: Math.random() * 100,
      size: 6 + Math.random() * 10,
      color: colors[Math.floor(Math.random() * colors.length)],
      delay: Math.random() * 1600,
      duration: 1800 + Math.random() * 1400,
      drift: (Math.random() - 0.5) * 220,
      rot: (Math.random() - 0.5) * 1400,
    });
  }
  return particles;
}

function JuiceLayer() {
  const [bursts, setBursts] = useState([]);
  const [rain, setRain] = useState([]);
  const [flashes, setFlashes] = useState([]);
  const [floats, setFloats] = useState([]);

  // Every timeout scheduled here is tracked so a StrictMode remount (or a
  // genuine unmount mid-animation) doesn't leave orphaned timers writing to
  // unmounted state.
  const timers = useRef(new Set());

  const later = useCallback((fn, ms) => {
    const handle = setTimeout(() => {
      timers.current.delete(handle);
      fn();
    }, ms);
    timers.current.add(handle);
  }, []);

  useEffect(() => {
    const root = document.getElementById('root');
    let shakeTimer = null;

    const unsubscribe = onJuice((event) => {
      switch (event.kind) {
        case 'burst': {
          const particles = makeBurstParticles(event);
          setBursts((prev) => [...prev, { id: event.id, particles }]);
          later(() => setBursts((prev) => prev.filter((b) => b.id !== event.id)), PARTICLE_MS + 200);
          break;
        }

        case 'rain': {
          const particles = makeRainParticles(event);
          setRain((prev) => [...prev, { id: event.id, particles }]);
          later(() => setRain((prev) => prev.filter((r) => r.id !== event.id)), RAIN_MS + 600);
          break;
        }

        case 'flash': {
          setFlashes((prev) => [...prev, { id: event.id, color: event.color, duration: event.duration }]);
          later(() => setFlashes((prev) => prev.filter((f) => f.id !== event.id)), event.duration + 60);
          break;
        }

        case 'float': {
          setFloats((prev) => [...prev, event]);
          later(() => setFloats((prev) => prev.filter((f) => f.id !== event.id)), FLOAT_MS);
          break;
        }

        case 'shake': {
          if (!root) break;
          const className = `is-shaking-${event.strength}`;
          // Restart cleanly if a second shake lands mid-animation, otherwise
          // the browser ignores the re-added class and the hit reads as lost.
          root.classList.remove('is-shaking-light', 'is-shaking-medium', 'is-shaking-heavy');
          void root.offsetWidth;
          root.classList.add(className);
          clearTimeout(shakeTimer);
          shakeTimer = setTimeout(
            () => root.classList.remove(className),
            SHAKE_MS[event.strength] ?? SHAKE_MS.medium
          );
          break;
        }

        default:
          break;
      }
    });

    return () => {
      unsubscribe();
      clearTimeout(shakeTimer);
      for (const handle of timers.current) clearTimeout(handle);
      timers.current.clear();
      root?.classList.remove('is-shaking-light', 'is-shaking-medium', 'is-shaking-heavy');
    };
  }, [later]);

  return (
    <div className="ui-juice" aria-hidden="true">
      {flashes.map((flash) => (
        <div
          key={flash.id}
          className="ui-juice__flash"
          style={{ background: flash.color, animationDuration: `${flash.duration}ms` }}
        />
      ))}

      {bursts.map((burst) =>
        burst.particles.map((p) => (
          <span
            key={p.key}
            className={`ui-juice__particle ${p.square ? 'is-square' : ''}`}
            style={{
              left: `${p.x}px`,
              top: `${p.y}px`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              background: p.color,
              boxShadow: `0 0 10px ${p.color}`,
              animationDelay: `${p.delay}ms`,
              '--dx': `${p.dx}px`,
              '--dy': `${p.dy}px`,
              '--rot': `${p.rot}deg`,
            }}
          />
        ))
      )}

      {rain.map((batch) =>
        batch.particles.map((p) => (
          <span
            key={p.key}
            className="ui-juice__confetti"
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size * 1.6}px`,
              background: p.color,
              boxShadow: `0 0 8px ${p.color}`,
              animationDelay: `${p.delay}ms`,
              animationDuration: `${p.duration}ms`,
              '--drift': `${p.drift}px`,
              '--rot': `${p.rot}deg`,
            }}
          />
        ))
      )}

      {floats.map((f) => (
        <span
          key={f.id}
          className={`ui-juice__float ui-juice__float--${f.tone}`}
          style={{ left: `${f.x}px`, top: `${f.y}px` }}
        >
          {f.text}
        </span>
      ))}
    </div>
  );
}

export default JuiceLayer;
